/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import {
  DialogContentText,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { IObject, TObjectFilter } from 'src/models/objectManagement';
import { reducerUpdateAnalyticArgumentObjectIdList } from 'src/features/analyticArguments';
import { useLocation } from 'react-router';
import CloseIcon from '@mui/icons-material/Close';
import useCustomDashboard from 'src/services/useCustomDashboard';
import { reducerUpdateDashboardList } from 'src/features/customDashboard';
import {
  reducerUpdateGroupObject,
  reducerUpdateObjectListByObjectType,
  reducerUpdateSelectedGroups,
  reducerUpdateSelectedObjectType
} from 'src/features/objectManagement';
import { SETTINGS_GROUP } from 'src/route';
import useObjectManagement from 'src/services/useObjectManagement';
import { useAlert } from 'src/hooks/useAlert';
import { objectTypeOptions } from 'src/content/settings/MonitoredAssets/components/HeaderObjects';
import { useInactiveObject } from 'src/hooks/useInactiveObject';
import { ADD_OBJECT_TO_GROUP_VALIDATION } from 'src/constants';
import { TSocialMedia } from 'src/models/general';
import { SOCIAL_MEDIA_LIST } from 'src/components/FilterObjectByPlatform';
import ProductTourTooltip from 'src/components/ProductTourTooltip';
import SelectObjectSection, {
  ITabValue
} from './components/SelectObjectSection';

interface Props {
  title?: string;
  isCampaign?: boolean;
  isHidden?: boolean;
  onCloseEdit?: () => void;
  isEditing?: boolean;
  isDashboard?: boolean;
  showFilter?: boolean;
  onClickNextTour?: () => void;
}

export interface ITabList {
  value: ITabValue;
  label: string;
}

export const defaultTabListNonRetail: ITabList[] = [
  {
    value: 'all',
    label: 'All'
  },
  {
    value: 'account',
    label: 'Social Profile'
  },
  {
    value: 'keyword',
    label: 'Keyword'
  },
  {
    value: 'hashtag',
    label: 'Hashtag'
  },
  {
    value: 'post_link',
    label: 'Post Link'
  }
];

export const defaultTabListRetail: ITabList[] = [
  {
    value: 'all',
    label: 'All'
  },
  {
    value: 'account',
    label: 'Social Profile'
  },
  {
    value: 'keyword',
    label: 'Keyword'
  },
  {
    value: 'hashtag',
    label: 'Hashtag'
  }
];

const ModalManageObjectRetail: React.FC<Props> = ({
  title,
  isCampaign = false,
  isHidden = false,
  isEditing = false,
  onCloseEdit,
  isDashboard = false,
  showFilter = false,
  onClickNextTour
}) => {
  const theme = useTheme();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const pathSocialMedia = pathname.split('/')[2];
  const mainPath = pathname.split('/')[1];
  const isGoogleReview = mainPath === 'google_review';
  const isStreamPanel = mainPath === 'stream_panel';
  const [open, setOpen] = useState(false);
  const [hide, setHide] = useState(false);
  const { objectManagementEditDashboard } = useCustomDashboard();
  const { objectManagementEditGroup } = useObjectManagement();
  const selectedObjectType = useAppSelector(
    (state) => state.objectManagement.selectedObjectType
  );
  const { handleClickAlert } = useAlert();
  const autoRefreshInterval = useAppSelector(
    (state) => state.autoRefreshMetric.selectedAutoRefreshInterval
  );
  const { isInactiveObjectExist } = useInactiveObject();

  const [selectedPlatforms, setSelectedPlatforms] =
    useState<TSocialMedia[]>(SOCIAL_MEDIA_LIST);

  const customerType = useAppSelector(
    (state) => state.users.project.customerType
  );
  const isRetail = customerType === 'b2c';
  const defaultTabList = isRetail
    ? defaultTabListRetail
    : defaultTabListNonRetail;

  const [tabList, setTabList] = useState<ITabList[]>(defaultTabList);
  const [selectedTab, setSelectedTab] = useState<ITabValue>('all');

  const handleClickOpen = () => {
    if (autoRefreshInterval > 0) {
      handleClickAlert({
        vertical: 'top',
        horizontal: 'center',
        severity: 'warning',
        message: 'You can manage objects only when auto refresh is off'
      });
      return;
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  const projectId = useAppSelector((state) => state.users.project.id);

  const { dashboardList, dashboardSelectedById } = useAppSelector(
    (state) => state.storeCustomDashboard
  );

  const objectList = useAppSelector(
    (state) => state.objectManagement.objectList.data
  );

  const filteredObjectsByType = useMemo(() => {
    if (objectList.length <= 0) return [];
    if (selectedObjectType === 'entity')
      return objectList.filter((object) => object.isEntity);
    if (selectedObjectType === 'object')
      return objectList.filter((object) => !object.isEntity);
    return objectList;
  }, [objectList, selectedObjectType]);

  const socialProfileObjList = useMemo(() => {
    return filteredObjectsByType.filter((obj) => {
      if (isGoogleReview) {
        return (
          obj.streamType === 'account' && obj.socialMedia === 'googleReview'
        );
      }
      if (isStreamPanel) {
        return obj.streamType === 'account';
      }
      return obj.streamType === 'account' && obj.socialMedia !== 'googleReview';
    });
  }, [filteredObjectsByType, isGoogleReview, isStreamPanel]);

  const keywordHashtagObjList = useMemo(() => {
    if (showFilter && !isGoogleReview) {
      return filteredObjectsByType.filter(
        (obj) =>
          obj.streamType === 'keyword' &&
          selectedPlatforms.includes(obj.socialMedia)
      );
    }
    return filteredObjectsByType.filter((obj) => obj.streamType === 'keyword');
  }, [filteredObjectsByType, selectedPlatforms, showFilter, isGoogleReview]);

  const keywordObjList = useMemo(() => {
    return keywordHashtagObjList.filter((obj) => !obj.isHashtag);
  }, [keywordHashtagObjList]);

  const hashtagObjList = useMemo(() => {
    // if (!isCampaign) return [];
    return keywordHashtagObjList.filter((obj) => obj.isHashtag);
  }, [keywordHashtagObjList]);

  const objectIdList = useAppSelector(
    (state) => state.analyticArguments.objectIdList
  );

  const {
    selectedGroups,
    groupSelectedById: selectedGroupId,
    groupObject: groupObjects
  } = useAppSelector((state) => state.objectManagement);

  // get group name by selected group id
  const selectedGroup = useMemo(() => {
    const selectedGroup = groupObjects.data.find(
      (group) => group.id === selectedGroupId
    );
    return selectedGroup ?? null;
  }, [selectedGroupId, groupObjects]);

  const [selectedGroupName, setSelectedGroupName] = useState<string>();

  useEffect(() => {
    setSelectedGroupName(selectedGroup?.displayName ?? '');
  }, [selectedGroup]);

  const activeObjectIds = useMemo(() => {
    const objectIdListSet = new Set(objectIdList);
    return objectList.filter((object) => objectIdListSet.has(object.id));
  }, [objectIdList]);

  const getActiveSocialProfileIds = () => {
    return activeObjectIds
      .filter((obj) => obj.streamType === 'account')
      .map((obj) => obj.id);
  };

  const getActiveKeywordAndHashtag = () => {
    return activeObjectIds
      .filter((object) => object.streamType === 'keyword')
      .map((object) => object.id);
  };

  const getActiveKeyword = () => {
    return activeObjectIds
      .filter((object) => object.streamType === 'keyword' && !object.isHashtag)
      .map((object) => object.id);
  };

  const getActiveHashtag = () => {
    return activeObjectIds
      .filter((object) => object.streamType === 'keyword' && object.isHashtag)
      .map((object) => object.id);
  };

  const [selectedSocialProfileIds, setSelectedSocialProfileIds] = useState<
    Array<string>
  >(getActiveSocialProfileIds);

  useEffect(() => {
    setSelectedSocialProfileIds(getActiveSocialProfileIds());
  }, [activeObjectIds]);

  const [selectedKeywordAndHashtagIds, setSelectedKeywordAndHashtagIds] =
    useState<Array<string>>(getActiveKeywordAndHashtag);

  useEffect(() => {
    setSelectedKeywordAndHashtagIds(getActiveKeywordAndHashtag());
  }, [activeObjectIds]);

  const [selectedKeywordIds, setSelectedKeywordIds] =
    useState<Array<string>>(getActiveKeyword);

  const [selectedObjectIds, setSelectedObjectIds] = useState<Array<string>>([]);

  useEffect(() => {
    setSelectedObjectIds(activeObjectIds.map((item) => item.id));
  }, [activeObjectIds]);

  useEffect(() => {
    setSelectedKeywordIds(getActiveKeyword());
  }, [activeObjectIds]);

  const [selectedHashtagIds, setSelectedHashtagIds] =
    useState<Array<string>>(getActiveHashtag);
  const [selectedPostLinkIds, setSelectedPostLinkIds] = useState<Array<string>>(
    []
  );

  // set active hashtag for campaign monitoring
  useEffect(() => {
    if (!isCampaign) return;
    const activeHashtagObjects = activeObjectIds.filter(
      (object) => object.isHashtag === true
    );
    const activeHashtagObjectIds = activeHashtagObjects.map(
      (object) => object.id
    );
    setSelectedHashtagIds(activeHashtagObjectIds);
  }, [isCampaign, activeObjectIds]);

  useEffect(() => {
    const selectedObjectIdsSocialProfile = objectList
      .filter(
        (item) =>
          item.streamType === 'account' && selectedObjectIds.includes(item.id)
      )
      .map((value) => value.id);
    const selectedObjectIdsKeywordHashtag = objectList
      .filter(
        (item) =>
          item.streamType === 'keyword' && selectedObjectIds.includes(item.id)
      )
      .map((value) => value.id);
    const selectedObjectIdsKeyword = objectList
      .filter(
        (item) =>
          item.streamType === 'keyword' &&
          !item.isHashtag &&
          selectedObjectIds.includes(item.id)
      )
      .map((value) => value.id);
    const selectedObjectIdsHashtag = objectList
      .filter((item) => item.isHashtag && selectedObjectIds.includes(item.id))
      .map((value) => value.id);
    const selectedObjectIdsPostLink = objectList
      .filter(
        (item) =>
          item.streamType === 'post_link' && selectedObjectIds.includes(item.id)
      )
      .map((value) => value.id);
    setSelectedSocialProfileIds(selectedObjectIdsSocialProfile);
    setSelectedKeywordAndHashtagIds(selectedObjectIdsKeywordHashtag);
    setSelectedKeywordIds(selectedObjectIdsKeyword);
    setSelectedHashtagIds(selectedObjectIdsHashtag);
    setSelectedPostLinkIds(selectedObjectIdsPostLink);
  }, [selectedObjectIds]);

  const isPageHaveAKeyword = () => {
    switch (pathSocialMedia) {
      case 'content':
        return false;
      case 'community':
        return false;
      default:
        return true;
    }
  };

  useEffect(() => {
    if (
      pathSocialMedia === 'content' ||
      pathSocialMedia === 'community' ||
      isGoogleReview
    ) {
      setTabList(defaultTabList.filter((item) => item.value === 'account'));
      setSelectedTab('account');
    } else if (pathSocialMedia === 'campaign') {
      setTabList(defaultTabList.filter((item) => item.value === 'hashtag'));
      setSelectedTab('hashtag');
    } else {
      setTabList(defaultTabList);
      setSelectedTab('all');
    }
  }, [pathSocialMedia, isGoogleReview, isRetail]);

  const handleClickDone = () => {
    const objectListActive = isCampaign
      ? selectedHashtagIds
      : isPageHaveAKeyword()
      ? selectedSocialProfileIds
          .concat(selectedKeywordAndHashtagIds)
          .concat(selectedPostLinkIds)
      : selectedSocialProfileIds;

    // edit group
    if (location.pathname === SETTINGS_GROUP) {
      if (isInactiveObjectExist(objectListActive)) {
        handleClickAlert({
          message: ADD_OBJECT_TO_GROUP_VALIDATION,
          severity: 'error',
          vertical: 'top',
          horizontal: 'center'
        });
        return;
      }

      const payload = {
        projectId: projectId,
        objectList: objectListActive,
        objectGroupId: selectedGroupId,
        groupName: selectedGroupName,
        templateName: 'content'
      };

      objectManagementEditGroup({ variables: payload }).then((response) => {
        const editedGroup = response.data.objectManagement_editObjectGroup;
        const updatedGroups = groupObjects.data.map((group) => {
          if (editedGroup.id === group.id) return { ...editedGroup };
          return group;
        });

        const isEditedGroupActive = selectedGroups.some(
          (group) => group.id === editedGroup.id
        );
        if (isEditedGroupActive) {
          dispatch(reducerUpdateSelectedGroups([editedGroup]));
        }

        dispatch(
          reducerUpdateGroupObject({ ...groupObjects, data: updatedGroups })
        );
        dispatch(reducerUpdateObjectListByObjectType(editedGroup.objectList));
        dispatch(
          reducerUpdateAnalyticArgumentObjectIdList(
            editedGroup.objectList.map((object) => object.id)
          )
        );
      });
      onCloseEdit();
      return;
    }

    // edit custom dashboard
    if (isDashboard && isEditing) {
      const tempDashboard = dashboardSelectedById;
      const payload = {
        displayName: tempDashboard.displayName,
        objectIds: objectListActive,
        projectId,
        customDashboardId: tempDashboard.id
      };
      objectManagementEditDashboard({
        variables: payload
      }).then((response) => {
        const { id, objectList } =
          response.data?.customDashboard_editCustomDashboard;
        const objectIdList = objectList.map((object: IObject) => object.id);
        const updateDashboard = dashboardList.data.map((dashboard) => {
          if (dashboard.id === id) {
            return { ...dashboard, objectList };
          }
          return dashboard;
        });
        dispatch(
          reducerUpdateDashboardList({
            ...dashboardList,
            data: updateDashboard
          })
        );
        dispatch(reducerUpdateObjectListByObjectType(objectList));
        dispatch(reducerUpdateAnalyticArgumentObjectIdList(objectIdList));
        isEditing ? onCloseEdit() : handleClose();
      });
      return;
    }

    // for add new group via group selection drop down
    if (isEditing) {
      if (isInactiveObjectExist(objectListActive)) {
        handleClickAlert({
          message: ADD_OBJECT_TO_GROUP_VALIDATION,
          severity: 'error',
          vertical: 'top',
          horizontal: 'center'
        });
        return;
      }

      const payload = {
        projectId: projectId,
        objectList: objectListActive,
        objectGroupId: selectedGroupId,
        groupName: selectedGroupName,
        templateName: 'content'
      };

      objectManagementEditGroup({ variables: payload }).then((res) => {
        const editedGroup = res.data.objectManagement_editObjectGroup;
        const updatedGroups = groupObjects.data.map((group) => {
          if (editedGroup.id === group.id) return { ...editedGroup };
          return group;
        });

        dispatch(reducerUpdateSelectedGroups([editedGroup]));
        dispatch(
          reducerUpdateGroupObject({ ...groupObjects, data: updatedGroups })
        );
      });
    }

    // for change active object via modal
    const newObjectList = objectList
      .filter((val) => {
        return objectListActive.includes(val.id);
      })
      .map((val) => {
        return val;
      });

    dispatch(reducerUpdateObjectListByObjectType(newObjectList));
    dispatch(reducerUpdateAnalyticArgumentObjectIdList(objectListActive));
    isEditing ? onCloseEdit() : handleClose();
  };

  const handleChangeObjectType = (
    event: SelectChangeEvent<string>,
    child: React.ReactNode
  ) => {
    const selectedValue = event.target.value;
    dispatch(reducerUpdateSelectedObjectType(selectedValue as TObjectFilter));
    const isGroupActive = selectedGroupId !== '_default';
    const currentObjectList = isGroupActive
      ? selectedGroup.objectList
      : objectList;

    const objectListByType = currentObjectList.filter((object) => {
      if (selectedValue === 'entity') return object.isEntity;
      if (selectedValue === 'object') return !object.isEntity;
      return true;
    });

    // set active objects based on object type
    setSelectedSocialProfileIds(
      objectListByType
        .filter((object) => object.streamType === 'account')
        .map((object) => object.id)
    );
    setSelectedKeywordAndHashtagIds(
      objectListByType
        .filter((object) => object.streamType === 'keyword')
        .map((object) => object.id)
    );
    if (isCampaign) {
      setSelectedHashtagIds(
        objectListByType
          .filter(
            (object) => object.streamType === 'keyword' && object.isHashtag
          )
          .map((obj) => obj.id)
      );
    }
    setSelectedObjectIds(objectListByType.map((item) => item.id));
  };

  const {
    isOnTour,
    currentTour: { stepNumber, submenu, menu }
  } = useAppSelector((state) => state.storeProductTour);

  const isCurrentlyOnTourGroupStep6 =
    stepNumber === 6 && isOnTour && submenu === 'group';

  const isCurrentlyOnTourSocmedOverviewStep11 =
    isOnTour &&
    stepNumber === 11 &&
    menu === 'social-media' &&
    submenu === 'overview';

  return (
    <>
      {isHidden ? null : (
        <Stack
          onClick={!isOnTour ? handleClickOpen : undefined}
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            backgroundColor: '#0B85AE',
            px: '11px',
            py: '8px',
            borderRadius: '16px',
            cursor: 'pointer',
            ':hover': {
              opacity: 0.5
            }
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.66667 3.33398H2"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M8 12.666H2"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M9.33398 2V4.66667"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M10.666 11.334V14.0007"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M14 8H8"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M13.9993 12.666H10.666"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M14.0007 3.33398H9.33398"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M5.33398 6.66602V9.33268"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M5.33333 8H2"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <Typography fontWeight={400} color="white">
            Manage {title ? title : 'Objects'}
          </Typography>
        </Stack>
      )}
      <Dialog
        onClose={
          isOnTour
            ? undefined
            : isEditing
            ? () => {
                setSelectedGroupName(selectedGroup?.displayName ?? '');
                onCloseEdit();
              }
            : handleClose
        }
        open={
          isCurrentlyOnTourSocmedOverviewStep11
            ? isCurrentlyOnTourSocmedOverviewStep11
            : isEditing
            ? isEditing
            : open
        }
        fullWidth
        maxWidth={'md'}
        sx={{
          opacity: hide ? 0 : 1,
          ...(isOnTour ? { pointerEvents: 'none' } : {})
        }}
        PaperProps={{
          sx: {
            borderRadius: '16px'
          }
        }}
      >
        <ProductTourTooltip
          placement="right"
          open={
            isCurrentlyOnTourGroupStep6 || isCurrentlyOnTourSocmedOverviewStep11
          }
          maxWidth={180}
          onClick={onClickNextTour}
          onClose={() => {
            if (onClickNextTour) {
              onClickNextTour();
            }
            handleClose();
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ p: 2 }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ px: 2 }}
            >
              <Typography variant="h4">
                Manage {title ? title : 'Objects'}
              </Typography>
              {!isEditing ? (
                <Select
                  id="objectType"
                  name="objectType"
                  value={selectedObjectType}
                  onChange={handleChangeObjectType}
                  sx={{ minWidth: '180px', borderRadius: '16px' }}
                >
                  <MenuItem
                    key="placeholder"
                    id="placeholder"
                    value=""
                    disabled
                  >
                    Object Type
                  </MenuItem>
                  {objectTypeOptions.map(({ id, value, label }) => (
                    <MenuItem key={id} id={id} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              ) : null}
            </Stack>
            <IconButton
              aria-label="close"
              onClick={
                isOnTour
                  ? undefined
                  : isEditing
                  ? () => {
                      setSelectedGroupName(selectedGroup?.displayName ?? '');
                      onCloseEdit();
                    }
                  : handleClose
              }
              sx={{
                color: (theme) => theme.palette.grey[500]
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </ProductTourTooltip>
        {isEditing && (
          <Stack sx={{ mb: theme.spacing(4), mx: theme.spacing(5) }}>
            <DialogContentText sx={{ mb: theme.spacing(0.5) }}>
              Group name
            </DialogContentText>
            <TextField
              variant="outlined"
              fullWidth
              value={selectedGroupName}
              onChange={(e) => setSelectedGroupName(e.target.value)}
            />
          </Stack>
        )}

        <SelectObjectSection
          objectList={filteredObjectsByType}
          activeObjectIdList={selectedObjectIds}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          tabList={tabList}
          onSelectObject={(objectIds: string[]) =>
            setSelectedObjectIds(objectIds)
          }
          isGoogleReview={isGoogleReview}
          isEditing={isEditing}
          isStreamPanel={isStreamPanel}
          handleDone={!isOnTour ? handleClickDone : () => {}}
          disableSubmit={false}
          isCustomDashboard={isDashboard}
        />
      </Dialog>
    </>
  );
};

export default ModalManageObjectRetail;
